import React from "react";
import { toast } from "sonner";
import { Loader2, Play, Truck, Package, ArrowRight } from "lucide-react";
import { adminApi } from "../../lib/api";

const STATUS_LABELS = {
  planned: "Planlagt",
  loaded: "Loaded",
  in_transit: "Undervejs",
  arrived: "Ankommet",
  closed: "Afsluttet",
};

export default function DistributionLinehaulPanel() {
  const [legs, setLegs] = React.useState([]);
  const [runs, setRuns] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState("");
  const [scanLabel, setScanLabel] = React.useState("");
  const [activeRun, setActiveRun] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [legsRes, runsRes] = await Promise.all([
        adminApi().get("/admin/distribution/linehaul/legs"),
        adminApi().get("/admin/distribution/linehaul/runs"),
      ]);
      setLegs(legsRes.data.items || []);
      setRuns(runsRes.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke hente linehaul");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const plan = async (fromHub, toHub) => {
    setBusy("plan");
    try {
      const { data } = await adminApi().post("/admin/distribution/linehaul/plan", {
        from_hub_id: fromHub,
        to_hub_id: toHub,
      });
      toast.success(data.message || "Linehaul planlagt");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Planlægning fejlede");
    } finally {
      setBusy("");
    }
  };

  const runAction = async (runId, action) => {
    setBusy(`${action}-${runId}`);
    try {
      const { data } = await adminApi().post(`/admin/distribution/linehaul/runs/${runId}/${action}`);
      toast.success(data.message || "OK");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Handling fejlede");
    } finally {
      setBusy("");
    }
  };

  const scan = async (runId, type) => {
    const label = scanLabel.trim();
    if (!label) return;
    setBusy(`scan-${type}`);
    try {
      const { data } = await adminApi().post(
        `/admin/distribution/linehaul/runs/${runId}/scan-${type}`,
        { label_number: label }
      );
      toast.success(data.message || "Scan OK");
      setScanLabel("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan fejlede");
    } finally {
      setBusy("");
    }
  };

  if (loading) {
    return (
      <div className="text-brand-text2">
        <Loader2 className="animate-spin inline mr-2" size={16} />
        Indlæser linehaul…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="distribution-linehaul-panel">
      <p className="text-sm text-brand-text2">
        Feeder → satellit (fx Køge → Bornholm / LF). Pakker med{" "}
        <code className="text-xs">awaiting_linehaul</code> på feeder-HUB.
      </p>

      <section className="space-y-3">
        <h3 className="font-semibold text-brand-dark flex items-center gap-2">
          <Truck size={18} />
          Linehaul-legs
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {legs.map((leg) => (
            <div key={`${leg.from_hub_id}-${leg.to_hub_id}`} className="bg-white border border-brand p-4">
              <div className="font-medium text-brand-dark flex items-center gap-2 flex-wrap">
                {leg.from_hub_name}
                <ArrowRight size={14} />
                {leg.to_hub_name}
              </div>
              <p className="text-xs text-brand-text2 mt-1">
                {leg.parcels_waiting} pakke(r) afventer linehaul
              </p>
              <button
                type="button"
                className="btn-primary mt-3 text-sm"
                disabled={busy === "plan" || leg.parcels_waiting === 0}
                onClick={() => plan(leg.from_hub_id, leg.to_hub_id)}
                data-testid={`linehaul-plan-${leg.to_hub_id}`}
              >
                {busy === "plan" ? <Loader2 className="animate-spin" size={14} /> : <Play size={14} />}
                Opret linehaul-run
              </button>
            </div>
          ))}
        </div>
        {legs.length === 0 && <p className="text-sm text-brand-text2">Ingen linehaul-legs konfigureret</p>}
      </section>

      <section className="space-y-3">
        <h3 className="font-semibold text-brand-dark flex items-center gap-2">
          <Package size={18} />
          Linehaul-runs
        </h3>
        {runs.length === 0 ? (
          <p className="text-sm text-brand-text2">Ingen runs endnu</p>
        ) : (
          <div className="space-y-3">
            {runs.map((run) => (
              <div key={run.id} className="bg-white border border-brand p-4" data-testid={`linehaul-run-${run.id}`}>
                <div className="flex flex-wrap justify-between gap-2">
                  <div>
                    <div className="font-semibold">{run.run_number}</div>
                    <div className="text-xs text-brand-text2">
                      {run.from_hub_name} → {run.to_hub_name} · {run.parcel_count} pakke(r)
                    </div>
                  </div>
                  <span className="text-xs px-2 py-1 bg-brand-bg2">{STATUS_LABELS[run.status] || run.status}</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {run.status === "planned" || run.status === "loaded" ? (
                    <>
                      <button type="button" className="btn-secondary text-xs" disabled={!!busy} onClick={() => { setActiveRun(run.id); }}>
                        Scan load
                      </button>
                      <button type="button" className="btn-primary text-xs" disabled={!!busy} onClick={() => runAction(run.id, "depart")}>
                        Afgå
                      </button>
                    </>
                  ) : null}
                  {run.status === "in_transit" ? (
                    <button type="button" className="btn-primary text-xs" disabled={!!busy} onClick={() => runAction(run.id, "arrive")}>
                      Ankommet
                    </button>
                  ) : null}
                  {run.status === "arrived" || run.status === "in_transit" ? (
                    <button type="button" className="btn-secondary text-xs" disabled={!!busy} onClick={() => setActiveRun(run.id)}>
                      Scan unload
                    </button>
                  ) : null}
                </div>
                {activeRun === run.id && (
                  <div className="flex gap-2 mt-3">
                    <input
                      className="input-flat flex-1 text-sm"
                      placeholder="Scan label"
                      value={scanLabel}
                      onChange={(e) => setScanLabel(e.target.value)}
                      data-testid="linehaul-scan-input"
                    />
                    <button type="button" className="btn-secondary text-xs" disabled={!!busy} onClick={() => scan(run.id, "load")}>
                      Load
                    </button>
                    <button type="button" className="btn-primary text-xs" disabled={!!busy} onClick={() => scan(run.id, "unload")}>
                      Unload
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
